import { useEffect, useRef, useState } from 'react';
import { Disc, Headphones, Mic, MicOff, Video, Volume2, X } from 'lucide-react';
import { useI18n } from '../../lib/i18n';
import { micConstraints } from '../../lib/share/capture';
import { browserName } from '../../lib/share/peer';
import { systemAudioSupported } from '../../lib/share/mixer';
import { docPipSupported } from '../../lib/share/pip';

/**
 * "Record your screen" (spec §4.12.1 punto 1): qué grabar lo decide el picker nativo en el paso
 * siguiente; acá se eligen el micrófono (con medidor de nivel), el audio del sistema (deshabilitado
 * con `share.systemAudioNA` en Safari/Firefox) y —solo si F0 lo aprueba— la cámara en burbuja.
 *
 * El clic de "Start recording" tiene que llegar al provider en el MISMO tick (activación
 * transitoria): acá se para el mic de la vista previa de forma síncrona y se llama `onStart`.
 *
 * `REC_CAMERA_BUBBLE`: la burbuja de cámara queda detrás de esta constante hasta que F0 confirme
 * que la ventana Document PiP sale en la grabación del monitor (spec §0 "Record: cámara").
 */
export const REC_CAMERA_BUBBLE = false;
/** Cuánto se espera al micrófono de la vista previa antes de dejar empezar igual. */
const MIC_PROBE_TIMEOUT_MS = 4_000;

export type RecordSetup = {
  withMic: boolean;
  micDeviceId: string | null;
  withSystemAudio: boolean;
  camera: boolean;
};

type Device = { deviceId: string; label: string };

export function RecordSetupModal({ onStart, onClose }: { onStart: (s: RecordSetup) => void; onClose: () => void }) {
  const { t } = useI18n();
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const sysOk = systemAudioSupported(ua);
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [micId, setMicId] = useState<string>('');
  // 'unknown' = el navegador no contestó en 4 s (prompt del SO colgado, headless sin dispositivo):
  // se puede empezar igual; la captura vuelve a pedir el mic en el gesto de Start.
  const [micState, setMicState] = useState<'probing' | 'ok' | 'denied' | 'none' | 'unknown'>('probing');
  const [withMic, setWithMic] = useState(true);
  const [withSys, setWithSys] = useState(sysOk);
  const [camera, setCamera] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const meterRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<HTMLButtonElement>(null);

  const stopPreview = () => {
    if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    for (const tr of streamRef.current?.getTracks() ?? []) { try { tr.stop(); } catch { /* */ } }
    streamRef.current = null;
    if (ctxRef.current) { void ctxRef.current.close().catch(() => {}); ctxRef.current = null; }
  };

  // Vista previa del micrófono elegido: abre el stream, lista los dispositivos (las etiquetas
  // solo aparecen con permiso concedido) y mueve el medidor de nivel por rAF sin re-renderizar.
  useEffect(() => {
    let alive = true;
    stopPreview();
    if (!withMic) { setMicState('none'); return undefined; }
    setMicState('probing');
    const probeTimeout = window.setTimeout(() => { if (alive) setMicState((st) => (st === 'probing' ? 'unknown' : st)); }, MIC_PROBE_TIMEOUT_MS);
    void (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: micConstraints(micId || undefined) });
        clearTimeout(probeTimeout);
        if (!alive) { for (const tr of s.getTracks()) tr.stop(); return; }
        streamRef.current = s;
        setMicState('ok');
        try {
          const list = await navigator.mediaDevices.enumerateDevices();
          if (alive) setDevices(list.filter((d) => d.kind === 'audioinput').map((d, i) => ({ deviceId: d.deviceId, label: d.label || `${t('rec.mic')} ${i + 1}` })));
        } catch { if (alive) setDevices([]); }
        try {
          const ctx = new AudioContext();
          ctxRef.current = ctx;
          const an = ctx.createAnalyser();
          an.fftSize = 512;
          ctx.createMediaStreamSource(s).connect(an);
          const buf = new Uint8Array(an.fftSize);
          const loop = () => {
            an.getByteTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i += 1) { const v = (buf[i]! - 128) / 128; sum += v * v; }
            const rms = Math.sqrt(sum / buf.length);
            const pct = Math.min(100, Math.round(rms * 260));
            if (meterRef.current) meterRef.current.style.width = `${pct}%`;
            rafRef.current = requestAnimationFrame(loop);
          };
          rafRef.current = requestAnimationFrame(loop);
        } catch { /* sin medidor; el mic igual sirve */ }
      } catch (e) {
        clearTimeout(probeTimeout);
        if (!alive) return;
        const name = (e as { name?: string })?.name;
        setMicState(name === 'NotFoundError' ? 'none' : name === 'OverconstrainedError' ? 'ok' : 'denied');
        if (name === 'OverconstrainedError') setMicId('');
        setDevices((d) => d ?? []);
      }
    })();
    return () => { alive = false; clearTimeout(probeTimeout); stopPreview(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micId, withMic]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    startRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const start = () => {
    // Síncrono: soltar el mic de la vista previa y arrancar dentro del mismo gesto.
    stopPreview();
    onStart({ withMic: withMic && micState !== 'denied' && micState !== 'none', micDeviceId: micId || null, withSystemAudio: withSys && sysOk, camera: camera && REC_CAMERA_BUBBLE });
  };

  const browser = browserName(ua) ?? t('share.yourBrowser');
  const showCamera = REC_CAMERA_BUBBLE && docPipSupported();

  return (
    <div className="ash-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ash-modal rec-setup" role="dialog" aria-modal="true" aria-label={t('rec.setupTitle')}>
        <div className="ash-modal-head">
          <b style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Video size={17} strokeWidth={1.9} />{t('rec.setupTitle')}</b>
          <button type="button" className="btn ghost sm ash-open" onClick={onClose} aria-label={t('common.close')}><X size={15} strokeWidth={1.75} /></button>
        </div>
        <p className="ss-hint">{t('rec.setupHint')}</p>

        <div className="rec-setup-rows">
          {/* Micrófono */}
          <div className="rec-setup-row">
            <label className="rec-setup-label" htmlFor="rec-mic"><Mic size={14} strokeWidth={2} aria-hidden /> {t('rec.mic')}</label>
            <div className="rec-setup-ctl">
              <select id="rec-mic" className="input sm" value={withMic ? micId : '__off'} disabled={micState === 'denied'}
                onChange={(e) => { const v = e.target.value; if (v === '__off') setWithMic(false); else { setWithMic(true); setMicId(v); } }}>
                {(devices ?? []).length === 0 && <option value="">{micState === 'none' && withMic ? t('rec.micNone') : t('rec.micDefault')}</option>}
                {(devices ?? []).map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
                <option value="__off">{t('rec.micOff')}</option>
              </select>
              <div className={`rec-meter${withMic && micState === 'ok' ? '' : ' off'}`} aria-hidden><div ref={meterRef} className="rec-meter-bar" /></div>
              {micState === 'denied' && <span className="rec-setup-note warn"><MicOff size={12} strokeWidth={2} aria-hidden /> {t('share.micDenied')}</span>}
              {withMic && micState === 'none' && <span className="rec-setup-note warn"><MicOff size={12} strokeWidth={2} aria-hidden /> {t('rec.noMic')}</span>}
            </div>
          </div>

          {/* Audio del sistema */}
          <div className="rec-setup-row">
            <label className="rec-setup-label" htmlFor="rec-sys"><Volume2 size={14} strokeWidth={2} aria-hidden /> {t('rec.systemAudio')}</label>
            <div className="rec-setup-ctl">
              <label className="rec-switch">
                <input id="rec-sys" type="checkbox" checked={withSys && sysOk} disabled={!sysOk} onChange={(e) => setWithSys(e.target.checked)} />
                <span className="rec-switch-track" aria-hidden><span className="rec-switch-knob" /></span>
                <span className="rec-setup-note">{sysOk ? t('rec.systemAudioHint') : t('share.systemAudioNA', { browser })}</span>
              </label>
            </div>
          </div>

          {/* Cámara en burbuja (detrás de REC_CAMERA_BUBBLE hasta el go de F0) */}
          {showCamera && (
            <div className="rec-setup-row">
              <label className="rec-setup-label" htmlFor="rec-cam"><Video size={14} strokeWidth={2} aria-hidden /> {t('rec.camera')}</label>
              <div className="rec-setup-ctl">
                <label className="rec-switch">
                  <input id="rec-cam" type="checkbox" checked={camera} onChange={(e) => setCamera(e.target.checked)} />
                  <span className="rec-switch-track" aria-hidden><span className="rec-switch-knob" /></span>
                </label>
              </div>
            </div>
          )}
        </div>

        <p className="ss-hint soft"><Headphones size={13} strokeWidth={2} aria-hidden /> {t('share.headphones')}</p>
        <div className="rec-setup-actions">
          <button type="button" className="btn ghost sm" onClick={onClose}>{t('common.cancel')}</button>
          <button ref={startRef} type="button" className="btn sm rec-start" onClick={start} disabled={withMic && micState === 'probing'}>
            <Disc size={14} strokeWidth={2.5} /> {t('rec.start')}
          </button>
        </div>
      </div>
    </div>
  );
}
